import React, { createContext, useContext, useState, ReactNode } from 'react';

interface Language {
  language_name: string;
  iso639_tag: string;
  flag: string;
}

interface LanguageContextType {
  selectedLanguage: Language | null;
  setSelectedLanguage: (language: Language) => void;
  availableLanguages: Language[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * Supported languages with full ISO 639-1 codes (language-REGION format).
 * Must match backend SupportedLanguage enum in users/language_constants.py
 */
export const availableLanguages: Language[] = [
  { language_name: 'English', iso639_tag: 'en-US', flag: '🇺🇸' },
  { language_name: 'Español', iso639_tag: 'es-ES', flag: '🇪🇸' },
  { language_name: 'Deutsch', iso639_tag: 'de-DE', flag: '🇩🇪' },
];

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [selectedLanguage, setSelectedLanguageState] = useState<Language | null>(() => {
    const saved = localStorage.getItem('selectedTargetLanguage');

    if (saved) {
      let languageTag: string | null = saved;

      try {
        const parsed = JSON.parse(saved);
        if (typeof parsed === 'string') {
          languageTag = parsed;
        } else if (parsed && typeof parsed === 'object' && 'iso639_tag' in parsed) {
          const iso = (parsed as { iso639_tag?: string }).iso639_tag;
          if (typeof iso === 'string') {
            languageTag = iso;
          }
        }
      } catch {
        // If parsing fails, fall back to treating the saved value as a plain string.
      }

      const persistedLanguage = availableLanguages.find(
        (language) => language.iso639_tag === languageTag
      );

      if (persistedLanguage) {
        return persistedLanguage;
      }
    }

    return availableLanguages[0];
  });

  const setSelectedLanguage = (language: Language) => {
    setSelectedLanguageState(language);
    localStorage.setItem(
      'selectedTargetLanguage',
      JSON.stringify({ iso639_tag: language.iso639_tag })
    );
  };

  const value: LanguageContextType = {
    selectedLanguage,
    setSelectedLanguage,
    availableLanguages,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
